<template>
  <!-- Manages EUMETSAT cloud WMS tile layers -->
</template>

<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import type OLMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';

import { useFilterStore } from '@/composables/useFilterStore';
import { useMap } from '@/composables/useMap';

const { map } = useMap();
const {
  timeRange,
  eumetsatTrueRgbVisible,
  eumetsatTrueRgbOpacity,
  eumetsatInfraredVisible,
  eumetsatInfraredOpacity,
} = useFilterStore();

const EUMETSAT_WMS_URL = 'https://view.eumetsat.int/geoserver/ows';
const EUMETSAT_ATTRIBUTION = '&copy; <a href="https://www.eumetsat.int/">EUMETSAT</a>';
const DEFAULT_CLOUD_TIME_STEP_MS = 3 * 60 * 60 * 1000;

interface CloudTimeMetadata {
  minMs: number;
  maxMs: number;
  stepMs: number;
}

interface CloudLayerConfig {
  key: 'trueRgb' | 'infrared';
  layerName: string;
  capabilitiesLayerName: string;
  capabilitiesUrl: string;
  zIndex: number;
}

const CLOUD_LAYER_CONFIGS: ReadonlyArray<CloudLayerConfig> = [
  {
    key: 'trueRgb',
    layerName: 'mumi:wideareacoverage_rgb_natural',
    capabilitiesLayerName: 'mumi:wideareacoverage_rgb_natural',
    capabilitiesUrl: 'https://view.eumetsat.int/geoserver/wms?service=WMS&request=GetCapabilities&version=1.3.0',
    zIndex: 20,
  },
  {
    key: 'infrared',
    layerName: 'mumi:worldcloudmap_ir108',
    capabilitiesLayerName: 'worldcloudmap_ir108',
    capabilitiesUrl: 'https://view.eumetsat.int/geoserver/mumi/worldcloudmap_ir108/ows?service=WMS&request=GetCapabilities&version=1.3.0',
    zIndex: 21,
  },
];

const cloudTimeMetadata = new Map<CloudLayerConfig['key'], CloudTimeMetadata>();
const capabilitiesInFlight = new Map<CloudLayerConfig['key'], Promise<void>>();

function parseIsoUtcMs(value: string | null | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseIsoDurationMs(value: string | null | undefined): number | null {
  const raw = String(value ?? '').trim();
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/i.exec(raw);
  if (!match) return null;

  const days = Number(match[1] ?? 0);
  const hours = Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);
  const seconds = Number(match[4] ?? 0);
  const totalMs = (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000;
  return totalMs > 0 ? totalMs : null;
}

function getDirectChildText(parent: Element, localName: string): string {
  for (const child of Array.from(parent.children)) {
    if (child.localName === localName) {
      return child.textContent?.trim() ?? '';
    }
  }
  return '';
}

function parseLayerTimeMetadata(xmlText: string, layerName: string): CloudTimeMetadata | null {
  if (typeof DOMParser === 'undefined') return null;

  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'text/xml');
  const layers = Array.from(xml.getElementsByTagNameNS('*', 'Layer'));
  const targetLayer = layers.find((layer) => getDirectChildText(layer, 'Name') === layerName) ?? null;
  if (!targetLayer) return null;

  const dimension = Array.from(targetLayer.children).find(
    (child) => child.localName === 'Dimension' && (child.getAttribute('name') ?? '').toLowerCase() === 'time',
  ) ?? null;
  if (!dimension) return null;

  const rangeText = dimension.textContent?.trim() ?? '';
  const [startIso, endIso, stepIso] = rangeText.split('/');
  const defaultIso = dimension.getAttribute('default');

  const minMs = parseIsoUtcMs(startIso);
  const maxMs = parseIsoUtcMs(defaultIso) ?? parseIsoUtcMs(endIso);
  const stepMs = parseIsoDurationMs(stepIso) ?? DEFAULT_CLOUD_TIME_STEP_MS;
  if (minMs == null || maxMs == null || maxMs < minMs) return null;

  return { minMs, maxMs, stepMs };
}

function clampCloudTime(epochMs: number, metadata: CloudTimeMetadata): string {
  const rawMs = Number.isFinite(epochMs) ? epochMs : Date.now();
  const clampedToRangeMs = Math.min(Math.max(rawMs, metadata.minMs), metadata.maxMs);
  const offsetMs = Math.max(0, clampedToRangeMs - metadata.minMs);
  const snappedMs = metadata.minMs + Math.floor(offsetMs / metadata.stepMs) * metadata.stepMs;
  return new Date(Math.min(snappedMs, metadata.maxMs)).toISOString();
}

function createCloudLayer(config: CloudLayerConfig): TileLayer<TileWMS> {
  const source = new TileWMS({
    url: EUMETSAT_WMS_URL,
    attributions: EUMETSAT_ATTRIBUTION,
    crossOrigin: 'anonymous',
    hidpi: true,
    serverType: 'geoserver',
    transition: 0,
    wrapX: true,
    params: {
      LAYERS: config.layerName,
      FORMAT: 'image/png',
      STYLES: 'raster',
      TRANSPARENT: true,
      TILED: true,
      VERSION: '1.3.0',
    },
  });

  const layer = new TileLayer({
    source,
    visible: false,
    opacity: 0.85,
  });
  layer.setZIndex(config.zIndex);
  return layer;
}

const trueRgbLayer = createCloudLayer(CLOUD_LAYER_CONFIGS[0]);
const infraredLayer = createCloudLayer(CLOUD_LAYER_CONFIGS[1]);
const layerByKey: Record<CloudLayerConfig['key'], TileLayer<TileWMS>> = {
  trueRgb: trueRgbLayer,
  infrared: infraredLayer,
};
const cloudLayers = [trueRgbLayer, infraredLayer] as const;

async function ensureCloudCapabilities(config: CloudLayerConfig): Promise<void> {
  if (cloudTimeMetadata.has(config.key)) return;
  const inFlight = capabilitiesInFlight.get(config.key);
  if (inFlight) return inFlight;

  const task = (async () => {
    const response = await fetch(config.capabilitiesUrl);
    if (!response.ok) {
      throw new Error(`Capabilities fetch failed (${response.status}) for ${config.layerName}`);
    }

    const xmlText = await response.text();
    const metadata = parseLayerTimeMetadata(xmlText, config.capabilitiesLayerName);
    if (!metadata) {
      throw new Error(`Missing time dimension metadata for ${config.layerName}`);
    }

    cloudTimeMetadata.set(config.key, metadata);
  })()
    .catch((err) => {
      console.warn(`Failed to load EUMETSAT capabilities for ${config.layerName}:`, err);
    })
    .finally(() => {
      capabilitiesInFlight.delete(config.key);
      syncCloudTime(timeRange.value[0]);
    });

  capabilitiesInFlight.set(config.key, task);
  return task;
}

function addLayers(targetMap: OLMap): void {
  for (const layer of cloudLayers) {
    targetMap.addLayer(layer);
  }
}

function removeLayers(targetMap: OLMap | null | undefined): void {
  if (!targetMap) return;
  for (const layer of [...cloudLayers].reverse()) {
    if (targetMap.getLayers().getArray().includes(layer)) {
      targetMap.removeLayer(layer);
    }
  }
}

function syncCloudTime(epochMs: number): void {
  for (const config of CLOUD_LAYER_CONFIGS) {
    const metadata = cloudTimeMetadata.get(config.key);
    if (!metadata) continue;
    const timeParam = clampCloudTime(epochMs, metadata);
    layerByKey[config.key].getSource()?.updateParams({ TIME: timeParam });
  }
}

watch(
  map,
  (nextMap, prevMap) => {
    if (prevMap) removeLayers(prevMap);
    if (nextMap) addLayers(nextMap);
  },
  { immediate: true },
);

watch(eumetsatTrueRgbVisible, (visible) => {
  trueRgbLayer.setVisible(visible);
  if (visible) void ensureCloudCapabilities(CLOUD_LAYER_CONFIGS[0]);
}, { immediate: true });

watch(eumetsatTrueRgbOpacity, (opacity) => {
  trueRgbLayer.setOpacity(opacity);
}, { immediate: true });

watch(eumetsatInfraredVisible, (visible) => {
  infraredLayer.setVisible(visible);
  if (visible) void ensureCloudCapabilities(CLOUD_LAYER_CONFIGS[1]);
}, { immediate: true });

watch(eumetsatInfraredOpacity, (opacity) => {
  infraredLayer.setOpacity(opacity);
}, { immediate: true });

watch(
  () => timeRange.value[0],
  (startMs) => {
    syncCloudTime(startMs);
  },
  { immediate: true },
);

for (const config of CLOUD_LAYER_CONFIGS) {
  void ensureCloudCapabilities(config);
}

onBeforeUnmount(() => {
  removeLayers(map.value);
});
</script>
